import {
  DatabaseUserType,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { useUser } from '../context';
import { toUserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { isBbUserView, isCnUserView } from '../../utils/partnerUser';

const logoSrc = '/images/logo-icon.png';

export const getStaticUserType = (): UserType => {
  const { pathname } = window.location;
  if (pathname.includes('/npo/')) return 'npo';
  if (pathname.includes('/donor/')) return 'donor';
  return 'ti staff';
};

export type ReportVisibility = 'PUBLIC' | 'RESTRICTED';
export type AccountVisibility =
  | 'UNDECIDED'
  | 'SKIPPED'
  | 'PUBLIC_DEFAULT'
  | 'RESTRICTED_DEFAULT'
  | 'CUSTOM';
export interface CognitoUser {
  attributes: {
    ['custom:account_id']: string; // '11521'
    ['custom:type']: 'admin' | 'basic';
    ['custom:user_id']: number;
    email: null | string; // string only when ghosted otherwise its null
    familyName: string;
    givenName: string;
    organizationName: string;
    organizationLogoUrl: string | null;
    organizationLogo: string | null;
    userLogo: string | null;
    visibility: AccountVisibility;
    newProgramVisibility: ReportVisibility;
    features?: Record<string, boolean>;
  };
  effectiveUser: {
    attributes: CognitoUser['attributes'];
  };
  authUser: {
    username: string;
    attributes: CognitoUser['attributes'];
  };
  password: string;
  ghostedUsername: null | string; // NPO's username when ghosted; htemuri+npo@trueimpact.com
  role: 'grantee' | 'funder';
}

interface UserActions {
  user: CognitoUser;
  ghostedUsername?: string;
  isGhosting: boolean;
  isAdmin: boolean;
  isDonor: boolean;
  isNPO: boolean;
  email: string;
  name: string;
  familyName: string;
  givenName: string;
  type: DatabaseUserType;
  userType: UserType;
  userStaticType: UserType;
  userId: string | number;
  accountId: string;
  organizationName: string;
  organizationLogo: string;
  visibility: AccountVisibility;
  isCNUser: boolean;
  isBBUser: boolean;
  isTIUser: boolean;
  isThirdParty: boolean;
  newProgramVisibility: ReportVisibility;
}

export const useUserActions = (): UserActions => {
  const user = useUser();
  const isBbPartner =
    isBbUserView() || !!user?.attributes?.features?.bb_partner;
  const isCnAuth0User = isCnUserView() && !isBbPartner;
  const isTIUser = !isBbPartner && !isCnAuth0User;
  const isThirdParty = isCnAuth0User || isBbPartner;
  const attrs = user?.attributes || ({} as CognitoUser['attributes']);
  const isAuthenticated = !!user && (!!user.authUser || !!user.effectiveUser);

  if (!isAuthenticated || !user?.attributes)
    return {
      user: { attributes: {} } as CognitoUser,
      isNPO: false,
      isAdmin: false,
      isDonor: false,
      isGhosting: false,
      email: '',
      name: '',
      familyName: '',
      givenName: '',
      isCNUser: isCnAuth0User,
      isBBUser: isBbPartner,
      isTIUser: isTIUser,
      isThirdParty,
      type: isCnAuth0User ? 'grantee' : ('' as DatabaseUserType),
      userType: isCnAuth0User ? 'cn-npo' : ('' as UserType),
      userStaticType: isCnAuth0User ? 'cn-npo' : ('' as UserType),
      userId: '',
      accountId: '',
      organizationName: 'Organization',
      organizationLogo: '',
      visibility: 'UNDECIDED',
      newProgramVisibility: 'RESTRICTED',
    };
  const org = attrs.organizationName || 'Organization';
  const organizationLogo =
    attrs.organizationLogo || attrs.organizationLogoUrl || logoSrc;
  const ghostedUsername = user.ghostedUsername || undefined;
  const accountId = attrs['custom:account_id'];
  const isAdmin = attrs['custom:type'] === 'admin' && accountId === '1';
  const userType = attrs['custom:type'] as DatabaseUserType;
  const familyName = attrs.familyName;
  const givenName = attrs.givenName;

  return {
    user,
    isNPO: isAdmin ? false : user.role === 'grantee',
    isDonor: isAdmin ? false : user.role === 'funder',
    isGhosting: Boolean(user.ghostedUsername),
    email: user.authUser?.username || attrs.email || '',
    familyName: familyName,
    givenName: givenName,
    type: userType,
    isCNUser: isCnAuth0User,
    isBBUser: isBbPartner,
    isTIUser: isTIUser,
    isThirdParty,
    userType: isCnAuth0User ? 'cn-npo' : toUserType(userType),
    userStaticType: isCnAuth0User ? 'cn-npo' : getStaticUserType(),
    name: `${givenName} ${familyName}`,
    ghostedUsername: ghostedUsername,
    userId: attrs['custom:user_id'],
    accountId,
    visibility: attrs.visibility,
    newProgramVisibility: attrs.newProgramVisibility,
    organizationName: org,
    organizationLogo,
    // it could be an admin NPO user, use isGhosting instead of isAdmin
    isAdmin: isAdmin,
  };
};
